import { FILESTORE_URL } from '@/settings';
import { store } from '@/store';
import { onAddMediaFile_Cn_Doc } from '@/store/actions';
import { CDSClient } from '@/sync/cds';
import { zwClient } from '@/sync/zapworks';
import { IFsFileData, IFsFileType } from '@r3f-component-data-structure';
import { IFileType } from '@zappar/zw-api-client';
import { FsAudio, FsExternalFile, FsFile, FsImage, FsImage360, FsImageTarget, FsVideo, FsVideo360, zmlToExternalFile } from './files';
import { FsModel3D } from './files/model3d';

/** Filestore object key (relative to `proj-<id>/<fileId>/`) that each media type is stored under. */
const PROJECT_FILE_KEY_BY_TYPE: Partial<Record<IFsFileType, string>> = {
    [IFsFileType.Image]: 'img',
    [IFsFileType.Video]: 'video.mp4',
    [IFsFileType.Model3d]: 'model.glb',
    [IFsFileType.Audio]: 'aud.mp3',
};

/**
 * Decodes the first frame of a video File into a PNG blob, used as the `frame0.png` thumbnail for a
 * video saved straight into the project filestore. Add-to-scene reads that thumbnail for the aspect
 * ratio (FsVideo.getAspectRatio), and the runtime uses it as a poster. Resolves null on failure.
 */
const generateVideoThumbnail = (file: File): Promise<Blob | null> => {
    return new Promise((resolve) => {
        const objectUrl = URL.createObjectURL(file);
        const video = document.createElement('video');
        video.muted = true;
        video.preload = 'auto';
        let settled = false;
        const finish = (blob: Blob | null) => {
            if (settled) return;
            settled = true;
            URL.revokeObjectURL(objectUrl);
            resolve(blob);
        };
        video.onloadeddata = () => {
            // Nudge past frame 0 so a decoded frame is guaranteed before we draw.
            try { video.currentTime = Math.min(0.1, video.duration || 0.1); } catch { finish(null); }
        };
        video.onseeked = () => {
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const ctx = canvas.getContext('2d');
            if (!ctx || canvas.width === 0 || canvas.height === 0) return finish(null);
            ctx.drawImage(video, 0, 0);
            canvas.toBlob((blob) => finish(blob), 'image/png');
        };
        video.onerror = () => finish(null);
        video.src = objectUrl;
    });
};

export class Filestore {
    private _cds: CDSClient | undefined
    private _jwt: string | undefined
    private _projectId: string | undefined
    private _files: {[id: string]: FsFile} = {}
    private _cacheBlobs: {[id: string]: Promise<Blob>} = {}
    private _cacheUrls: {[id: string]: string} = {}
    private _externalFilesById: { [id: string]: FsExternalFile } = {}
    private _cacheLatestAudio: { date: number, files: FsExternalFile[] } | undefined
    private _cache360Files: { date: number, files: FsExternalFile[] } | undefined

    constructor() {/**/}

    async init(cds: CDSClient, projectId: string) {
        this._cds = cds
        this._projectId = projectId
        const req = await this._cds.request(`${this._cds.getProjectUrl()}/filestore/jwt/`)
        let response: undefined | { token: string }
        try {
            response = await req.json()
        } catch(err) { console.error(err) }
        this._jwt = response?.token
        this._files = {}
        this._cacheBlobs = {}
        this._cacheUrls = {}
        this._externalFilesById = {}
    }

    save(fl: Blob, type: IFsFileType) {
        console.log('Filestore:save')
        const id = crypto.randomUUID()
        // TODO:
        //   1. get a jwt from CDS
        //   2. upload file to the store (S3)
        //   3. create a new media file object (based on the type)
        //   4. save to content doc
    }

    async importFromZml(zmlFileId: string) {
        const importResponse = await this._requestImportFromZml(zmlFileId)
        if (typeof importResponse === 'undefined') {
            console.error('Unable to import file from ZML:', zmlFileId)
            return
        }

        const fsFile = this._create(importResponse)
        store.dispatch(onAddMediaFile_Cn_Doc({ id: fsFile.id, type: fsFile.type, name: fsFile.name }))
        return fsFile
    }

    load<T extends FsFile>(fileId?: string) {
        if (typeof fileId === 'undefined') throw `Filestore: Unable to load undefined file ID`
        if (typeof this._files[fileId] !== 'undefined') return this._files[fileId] as T
        const filesById = store.getState().contentReducer.contentDoc.mediaFilesById
        const mediaFile = filesById[fileId]
        if (typeof mediaFile === 'undefined') throw `Filestore: File ${fileId} not found in content doc`
        this._files[fileId] = this._create<T>(mediaFile)
        return this._files[fileId] as T
    }

    private _create<T extends FsFile>(data: IFsFileData): T {
        if (typeof this._projectId === 'undefined') throw 'Filestore: Project ID is undefined'
        switch (data.type) {
            case IFsFileType.Image:
                return (new FsImage(data.id, this._projectId, data.name) as T)
            case IFsFileType.Model3d:
                return (new FsModel3D(data.id, this._projectId, data.name) as T)
            case IFsFileType.Video:
                return (new FsVideo(data.id, this._projectId, data.name) as T)
            case IFsFileType.Audio:
                return (new FsAudio(data.id, this._projectId, data.name) as T)
            case IFsFileType.Image360:
                return (new FsImage360(data.id, this._projectId, data.name) as T)
            case IFsFileType.Video360:
                return (new FsVideo360(data.id, this._projectId, data.name) as T)
            case IFsFileType.ImageTarget:
                return (new FsImageTarget(data.id, this._projectId, data.name) as T)
            default:
                throw `Filestore: Unknown media file type ${data.type}`
        }
    }

    async getFileBlob(url: string): Promise<Blob> {
        // Cache the in-flight Promise (not the resolved Blob) so concurrent callers for the same
        // url share a single fetch. Without this, N parallel callers each see a cache miss and
        // fire their own request before any of them settle. On failure the cache entry is dropped
        // so the next caller can retry.
        if (typeof this._cacheBlobs[url] !== 'undefined') return this._cacheBlobs[url]
        if (typeof this._jwt === 'undefined') throw 'Filestore: JWT is undefined'
        const jwt = this._jwt
        const promise = (async () => {
            const req = await fetch(url, { headers: [['X-Authorization', jwt]] })
            if (!req.ok) throw new Error(`Filestore: Unable to download file ${req.status} ${url}`)
            return req.blob()
        })()
        this._cacheBlobs[url] = promise
        promise.catch(() => { delete this._cacheBlobs[url] })
        return promise
    }

    /** Returns an already-cached blob promise for a url, or undefined. Does not trigger a fetch. */
    getCachedBlob(url: string): Promise<Blob> | undefined {
        return this._cacheBlobs[url]
    }

    getObjectUrl(id: string, blob: Blob | File) {
        if (typeof this._cacheUrls[id] !== 'undefined') return this._cacheUrls[id]
        if (blob.size === 0 || blob.type === '') console.warn('Filestore: Empty file', id)
        this._cacheUrls[id] = URL.createObjectURL(blob)
        return this._cacheUrls[id]
    }

    private async _requestImportFromZml(zmlFileId: string) {
        if (typeof this._cds === 'undefined') throw 'Filestore: CDS is undefined'
        const req = await this._cds.request(`${this._cds.getProjectUrl()}/filestore/zml-import/${zmlFileId}/`)
        let response: undefined | FsFile
        try {
            response = await req.json()
        } catch(err) {/**/}
        return response
    }

    async latestAudioFromZml(limit = 5) {
        if (typeof this._cacheLatestAudio === 'undefined' || Date.now() - this._cacheLatestAudio.date > 10000) {
            const workspaceId = store.getState().userReducer.project.accountId
            const resp = await zwClient.zml.search<IFileType.Audio>({ workspaceId, fileTypes: [IFileType.Audio], page: 1, limit })
            this._cacheLatestAudio = { date: Date.now(), files: resp.results.map(zmlToExternalFile) }
        }
        return this._cacheLatestAudio.files
    }

    clearAudioCache() {
        this._cacheLatestAudio = undefined
    }

    async latest360FilesFromZml(limit = 5) {
        if (typeof this._cache360Files === 'undefined' || Date.now() - this._cache360Files.date > 10000) {
            const workspaceId = store.getState().userReducer.project.accountId
            const resp = await zwClient.zml.search<IFileType.Image360 | IFileType.Video360>({ workspaceId, fileTypes: [IFileType.Image360, IFileType.Video360], page: 1, limit })
            this._cache360Files = { date: Date.now(), files: resp.results.map(zmlToExternalFile) }
        }
        return this._cache360Files.files
    }

    clear360FilesCache() {
        this._cache360Files = undefined
    }

    async saveImageTarget(imageFile: File, zptBinary: ArrayBuffer, name: string) {
        if (typeof this._projectId === 'undefined') throw 'Filestore: Project ID is undefined'
        if (typeof this._jwt === 'undefined') throw 'Filestore: JWT is undefined'

        const zptFileHeader = new Uint8Array(32)
        zptFileHeader.set(new TextEncoder().encode(`ZPT+IMG${zptBinary.byteLength};${imageFile.type}`))
        const concatZpt = new Uint8Array(zptFileHeader.byteLength + zptBinary.byteLength + imageFile.size)
        concatZpt.set(zptFileHeader, 0)
        const zptByteArray = new Uint8Array(zptBinary)
        concatZpt.set(zptByteArray, zptFileHeader.byteLength)
        concatZpt.set(new Uint8Array(await imageFile.arrayBuffer()), zptFileHeader.byteLength + zptByteArray.byteLength)

        const id = crypto.randomUUID()
        const url = `${FILESTORE_URL}/proj-${this._projectId}/${id}`;
        const reqHeaders = { 'X-Authorization': this._jwt };

        await Promise.all([
            await fetch(`${url}/img`, { headers: reqHeaders, method: 'PUT', body: imageFile }),
            await fetch(`${url}/zpt`, { headers: reqHeaders, method: 'PUT', body: concatZpt })
        ])

        this._cacheBlobs[`${url}/img`] = Promise.resolve(imageFile)
        store.dispatch(onAddMediaFile_Cn_Doc({ id, type: IFsFileType.ImageTarget, name }))
        return new FsImageTarget(id, this._projectId, name)
    }

    loadExternalFile(id: string) {
        return this._externalFilesById[id]
    }

    saveExternalFile(id: string, file: FsExternalFile) {
        this._externalFilesById[id] = file
    }

    /** Builds a filestore object URL the same way the Fs* file classes do, so cache keys line up. */
    private _buildProjectFileUrl(baseUrl: string, key: string) {
        const url = new URL(baseUrl)
        url.pathname = `${url.pathname}/${key}`
        return url.toString()
    }

    /**
     * Saves a media File straight into the project filestore (`proj-<id>/<id>/<key>`) and returns the
     * resulting FsFile, registered in the content doc. Used for external media-library assets so their
     * bytes live in the designer project instead of being uploaded to ZML/workspace storage.
     *
     * The uploaded blob is seeded into the blob cache so the editor renders without a round-trip
     * (mirrors saveImageTarget). For video, a `frame0.png` thumbnail is generated and stored too, as
     * add-to-scene reads it for the aspect ratio.
     */
    async saveProjectFile(id: string, file: File, type: IFsFileType, name: string): Promise<FsFile> {
        if (typeof this._projectId === 'undefined') throw 'Filestore: Project ID is undefined'
        if (typeof this._jwt === 'undefined') throw 'Filestore: JWT is undefined'

        const key = PROJECT_FILE_KEY_BY_TYPE[type]
        if (typeof key === 'undefined') throw `Filestore: Unsupported project file type ${type}`

        const baseUrl = `${FILESTORE_URL}/proj-${this._projectId}/${id}`
        const reqHeaders = { 'X-Authorization': this._jwt }
        const fileUrl = this._buildProjectFileUrl(baseUrl, key)

        await fetch(fileUrl, { headers: reqHeaders, method: 'PUT', body: file })
        this._cacheBlobs[fileUrl] = Promise.resolve(file)

        if (type === IFsFileType.Video) {
            const thumbnail = await generateVideoThumbnail(file)
            if (thumbnail) {
                const thumbUrl = this._buildProjectFileUrl(baseUrl, 'frame0.png')
                await fetch(thumbUrl, { headers: reqHeaders, method: 'PUT', body: thumbnail })
                // Seed the cache so FsVideo.getThumbnailBlob (used for the aspect ratio) resolves without
                // a round-trip. Directly-PUT files are private, so an unauthenticated GET would 401.
                this._cacheBlobs[thumbUrl] = Promise.resolve(thumbnail)
            }
        }

        store.dispatch(onAddMediaFile_Cn_Doc({ id, type, name }))
        return this.load(id)
    }
}

// Singletone
export const filestore = new Filestore()
